import {useEffect, useState, useSyncExternalStore} from 'react';

const DEFAULT_POLL_MS = 8000;

const REACHABILITY_ASSET_PATH = '/assets/layout/images/trr.svg';

export const NETWORK_CHAIN_POLL_INTERVAL_MS = 6500;

interface NetworkConnectionLike extends EventTarget {
    readonly effectiveType?: string;
    readonly downlink?: number;
    readonly rtt?: number;
}

type NavigatorWithConnection = Navigator & {
    connection?: NetworkConnectionLike;
    mozConnection?: NetworkConnectionLike;
    webkitConnection?: NetworkConnectionLike;
};

export function getConnection(): NetworkConnectionLike | undefined {
    if (typeof navigator === 'undefined') {
        return undefined;
    }
    const n = navigator as NavigatorWithConnection;
    return n.connection || n.mozConnection || n.webkitConnection;
}

export type NetworkQualityTier = 0 | 1 | 2 | 3;

export const NETWORK_QUALITY_COLORS: Record<NetworkQualityTier, string> = {
    0: '#e53935',
    1: '#ff8900',
    2: '#abdb06',
    3: '#22c55e',
};

export function getNetworkQualityTier(
    online: boolean,
    downlink: number | null | undefined,
    effectiveType: string | null | undefined,
): NetworkQualityTier {
    if (!online) {
        return 0;
    }

    const et = String(effectiveType ?? '').toLowerCase();
    const dl = downlink ?? null;

    const fromDownlink = (): NetworkQualityTier | null => {
        if (dl == null || Number.isNaN(dl)) {
            return null;
        }
        if (dl >= 9) {
            return 3;
        }
        if (dl >= 2.5) {
            return 2;
        }
        if (dl > 0) {
            return 1;
        }
        return 1;
    };

    if (et === '5g') {
        return 3;
    }
    if (et === 'lte') {
        return fromDownlink() ?? 3;
    }
    if (et === '4g') {
        const tier = fromDownlink();
        return tier ?? 2;
    }
    if (et === 'slow-4g' || et === '3g' || et === '2g' || et === 'slow-2g') {
        return 1;
    }

    const tier = fromDownlink();
    if (tier != null) {
        return tier;
    }

    return 2;
}

export function networkTierToSignalClass(
    tier: NetworkQualityTier,
): 'signal-icon-offline' | 'weak' | 'medium' | 'strong' {
    switch (tier) {
        case 0:
            return 'signal-icon-offline';
        case 1:
            return 'weak';
        case 2:
            return 'medium';
        case 3:
        default:
            return 'strong';
    }
}

export function useNetworkConnectionMetrics(): {downlink: number | null; effectiveType: string | null} {
    const [metrics, setMetrics] = useState(() => {
        const c = getConnection();
        return {
            downlink: c?.downlink ?? null,
            effectiveType: c?.effectiveType ?? null,
        };
    });

    useEffect(() => {
        const update = () => {
            const c = getConnection();
            setMetrics({
                downlink: c?.downlink ?? null,
                effectiveType: c?.effectiveType ?? null,
            });
        };

        update();
        window.addEventListener('online', update);
        window.addEventListener('offline', update);
        const conn = getConnection();
        conn?.addEventListener?.('change', update);
        const intervalId = window.setInterval(update, 3000);

        return () => {
            window.removeEventListener('online', update);
            window.removeEventListener('offline', update);
            conn?.removeEventListener?.('change', update);
            window.clearInterval(intervalId);
        };
    }, []);

    return metrics;
}

export function isPassiveNetworkReachabilityOk(): boolean {
    if (typeof navigator === 'undefined') {
        return true;
    }
    return navigator.onLine;
}

export async function probeNetworkReachabilityGetChain(timeoutMs = 4500): Promise<boolean> {
    return probeNetworkReachability(timeoutMs);
}

export async function probeNetworkReachability(timeoutMs = 4500): Promise<boolean> {
    if (typeof window === 'undefined') {
        return true;
    }
    if (!isPassiveNetworkReachabilityOk()) {
        return false;
    }
    try {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
        try {
            const url = new URL(REACHABILITY_ASSET_PATH, window.location.origin);
            url.searchParams.set('_net', String(Date.now()));
            const res = await fetch(url.toString(), {
                method: 'GET',
                cache: 'no-store',
                signal: controller.signal,
            });
            return res.ok;
        } finally {
            window.clearTimeout(timeoutId);
        }
    } catch {
        return false;
    }
}

let effectiveOnlineSnapshot: boolean =
    typeof navigator !== 'undefined' ? isPassiveNetworkReachabilityOk() : true;

const effectiveOnlineListeners = new Set<() => void>();

let teardownGlobalReachability: (() => void) | null = null;

function setEffectiveOnline(next: boolean) {
    if (effectiveOnlineSnapshot === next) {
        return;
    }
    effectiveOnlineSnapshot = next;
    effectiveOnlineListeners.forEach((notify) => notify());
}

function subscribeEffectiveOnline(onStoreChange: () => void) {
    effectiveOnlineListeners.add(onStoreChange);

    if (!teardownGlobalReachability) {
        let cancelled = false;

        const runProbe = async () => {
            if (!isPassiveNetworkReachabilityOk()) {
                if (!cancelled) {
                    setEffectiveOnline(false);
                }
                return;
            }
            const ok = await probeNetworkReachability();
            if (!cancelled) {
                setEffectiveOnline(ok);
            }
        };

        const sync = () => {
            if (cancelled) {
                return;
            }
            if (!isPassiveNetworkReachabilityOk()) {
                setEffectiveOnline(false);
                return;
            }
            void runProbe();
        };

        sync();

        const onOnline = () => sync();
        const onOffline = () => sync();
        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);

        const conn = getConnection();
        conn?.addEventListener?.('change', sync);

        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                sync();
            }
        };
        document.addEventListener('visibilitychange', onVisible);

        const intervalId = window.setInterval(sync, DEFAULT_POLL_MS);

        teardownGlobalReachability = () => {
            cancelled = true;
            window.removeEventListener('online', onOnline);
            window.removeEventListener('offline', onOffline);
            conn?.removeEventListener?.('change', sync);
            document.removeEventListener('visibilitychange', onVisible);
            window.clearInterval(intervalId);
            teardownGlobalReachability = null;
        };
    }

    return () => {
        effectiveOnlineListeners.delete(onStoreChange);
        if (effectiveOnlineListeners.size === 0) {
            teardownGlobalReachability?.();
        }
    };
}

function getEffectiveOnlineSnapshot() {
    return effectiveOnlineSnapshot;
}

function getEffectiveOnlineServerSnapshot() {
    return true;
}

export function useEffectiveOnlineStatus(_pollMs: number = DEFAULT_POLL_MS): boolean {
    return useSyncExternalStore(
        subscribeEffectiveOnline,
        getEffectiveOnlineSnapshot,
        getEffectiveOnlineServerSnapshot,
    );
}
